// Angular
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { JwtModule } from '@auth0/angular-jwt';
// Modules
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
// Services
import { AlertService } from './services/alert.service';
import { AuthGuardLogin } from './services/auth-guard-login.service';
import { AuthGuardAdmin } from './services/auth-guard-admin.service';
import { AuthService } from './services/auth.service';
import { ClientService } from './services/client.service';
import { EsService } from './services/es.service';
import { FeedbackService } from './services/feedback.service';
import { FileService } from './services/file.service';
import { GovernmentService } from './services/government.service';
import { GuidelineService } from './services/guideline.service';
import { LegislatureService } from './services/legislature.service';
import { MiscService } from './services/misc.service';
import { NotificationService } from './services/notification.service';
import { OrganizationService } from './services/organization.service';
import { PoliticianService } from './services/politician.service';
import { RuleService } from './services/rule.service';
import { UserService } from './services/user.service';
// Components
import { AccountComponent } from './account/account.component';
import { AddAlertFormComponent } from './search/add-alert-form/add-alert-form.component';
import { AddClientFormComponent } from './clients/add-client-form/add-client-form.component';
import { AddGuidelineFormComponent } from './guidelines/add-guideline-form/add-guideline-form.component';
import { AddMediaFormComponent } from './stakeholders/add-media-form/add-media-form.component';
import { AddOrganizationFormComponent } from './organizations/add-organization-form/add-organization-form.component';
import { AddRecordFormComponent } from './records/add-record-form/add-record-form.component';
import { AddRuleFormComponent } from './admin/add-rule-form/add-rule-form.component';
import { AddStakeholderFormComponent } from './stakeholders/add-stakeholder-form/add-stakeholder-form.component';
import { AdminComponent } from './admin/admin.component';
import { AlertComponent } from './search/alert/alert.component';
import { AlertsComponent } from './alerts/alerts.component';
import { AppComponent } from './app.component';
import { ClientComponent } from './clients/client/client.component';
import { ClientDetailsComponent } from './clients/client-details/client-details.component';
import { ClientsComponent } from './clients/clients.component';
import { FeedbackComponent } from './feedback/feedback.component';
import { ForgotPasswordFormComponent } from './login/forgot-password-form/forgot-password-form.component';
import { GuidelinesComponent } from './guidelines/guidelines.component';
import { LoginComponent } from './login/login.component';
import { LoginFormComponent } from './login/login-form/login-form.component';
import { LogoutComponent } from './logout/logout.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { OrganizationComponent } from './organizations/organization/organization.component';
import { OrganizationDetailsComponent } from './organizations/organization-details/organization-details.component';
import { OrganizationsComponent } from './organizations/organizations.component';
import { PdfComponent } from './pdf/pdf.component';
import { RecordComponent } from './records/record/record.component';
import { RecordDetailsComponent } from './records/record-details/record-details.component';
import { RecordsComponent } from './records/records.component';
import { RegisterComponent } from './register/register.component';
import { RegisterFormComponent } from './register/register-form/register-form.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { ResetPasswordFormComponent } from './reset-password/reset-password-form/reset-password-form.component';
import { SearchAlertComponent } from './search/search-alert/search-alert.component';
import { SearchComponent } from './search/search.component';
import { StatisticsComponent } from './statistics/statistics.component';
import { StakeholderComponent } from './stakeholders/stakeholder/stakeholder.component';
import { StakeholdersComponent } from './stakeholders/stakeholders.component';
import { StakeholderDetailsComponent } from './stakeholders/stakeholder-details/stakeholder-details.component';
import { TeamComponent } from './team/team.component';
import { UserComponent } from './team/user/user.component';
import { UserDetailsComponent } from './team/user-details/user-details.component';
// Modals
import { AddCommentModalComponent } from './shared/modals/add-comment-modal/add-comment-modal.component';
import { AddContractModalComponent } from './shared/modals/add-contract-modal/add-contract-modal.component';
import { AddGoalModalComponent } from './shared/modals/add-goal-modal/add-goal-modal.component';
import { EditAlertModalComponent } from './shared/modals/edit-alert-modal/edit-alert-modal.component';
import { EditClientDetailsModalComponent } from './shared/modals/edit-client-details-modal/edit-client-details-modal.component';
import { EditClientModalComponent } from './shared/modals/edit-client-modal/edit-client-modal.component';
import { EditCommentModalComponent } from './shared/modals/edit-comment-modal/edit-comment-modal.component';
import { EditGuidelineModalComponent } from './shared/modals/edit-guideline-modal/edit-guideline-modal.component';
import { EditOrganizationDetailsModalComponent } from './shared/modals/edit-organization-details-modal/edit-organization-details-modal.component';
import { EditOrganizationModalComponent } from './shared/modals/edit-organization-modal/edit-organization-modal.component';
import { EditRecordModalComponent } from './shared/modals/edit-record-modal/edit-record-modal.component';
import { EditRecordTimesheetModalComponent } from './shared/modals/edit-record-timesheet-modal/edit-record-timesheet-modal.component';
import { EditStakeholderDetailsModalComponent } from './shared/modals/edit-stakeholder-details-modal/edit-stakeholder-details-modal.component';
import { EditUserModalComponent } from './shared/modals/edit-user-modal/edit-user-modal.component';
import { OtherSourcesInfoModalComponent } from './shared/modals/other-sources-info-modal/other-sources-info-modal.component';
import { RecordTypeInfoModalComponent } from './shared/modals/record-type-info-modal/record-type-info-modal.component';
import { SearchQueryInfoModalComponent } from './shared/modals/search-query-info-modal/search-query-info-modal.component';
// Shared
import { AlertQueryComponent } from './shared/alert-query/alert-query.component';
import { BreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { DeleteBtnComponent } from './shared/delete-btn/delete-btn.component';
import { DeleteBtnAdminComponent } from './shared/delete-btn-admin/delete-btn-admin.component';
import { LoadingComponent } from './shared/loading/loading.component';
import { PaginationComponent } from './shared/pagination/pagination.component';
import { ResultComponent } from './shared/result/result.component';
import { SearchFilterComponent } from './shared/search-filter/search-filter.component';
import { SearchInputComponent } from './shared/search-input/search-input.component';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { SocialsComponent } from './shared/socials/socials.component';
import { SpinnerComponent } from './shared/spinner/spinner.component';
import { ToastComponent } from './shared/toast/toast.component';
// Pipes
import { FileSizePipe } from './shared/pipes/file-size.pipe';
import { FilterPipe } from './shared/pipes/filter.pipe';
import { FirstLetterUppercasePipe } from './shared/pipes/first-letter-uppercase.pipe';
import { IdCommissioneToRomanPipe } from './shared/pipes/idcommissione2roman.pipe';
import { LastnPipe } from './shared/pipes/lastn.pipe';
import { LinkifyPipe } from './shared/pipes/linkify.pipe';
import { RomanizePipe } from './shared/pipes/romanize.pipe';
import { Split2charsPipe } from './shared/pipes/split2chars.pipe';

@NgModule({
  declarations: [
    // Components
    AccountComponent,
    AddAlertFormComponent,
    AddClientFormComponent,
    AddGuidelineFormComponent,
    AddMediaFormComponent,
    AddOrganizationFormComponent,
    AddRecordFormComponent,
    AddRuleFormComponent,
    AddStakeholderFormComponent,
    AdminComponent,
    AlertComponent,
    AlertsComponent,
    AppComponent,
    ClientComponent,
    ClientDetailsComponent,
    ClientsComponent,
    FeedbackComponent,
    ForgotPasswordFormComponent,
    GuidelinesComponent,
    LoginComponent,
    LoginFormComponent,
    LogoutComponent,
    NotFoundComponent,
    NotificationsComponent,
    OrganizationComponent,
    OrganizationDetailsComponent,
    OrganizationsComponent,
    PdfComponent,
    RecordComponent,
    RecordDetailsComponent,
    RecordsComponent,
    RegisterComponent,
    RegisterFormComponent,
    ResetPasswordComponent,
    ResetPasswordFormComponent,
    SearchAlertComponent,
    SearchComponent,
    StatisticsComponent,
    StakeholderComponent,
    StakeholderDetailsComponent,
    StakeholdersComponent,
    TeamComponent,
    UserComponent,
    UserDetailsComponent,
    // Modals
    AddCommentModalComponent,
    AddContractModalComponent,
    AddGoalModalComponent,
    EditAlertModalComponent,
    EditClientDetailsModalComponent,
    EditClientModalComponent,
    EditCommentModalComponent,
    EditGuidelineModalComponent,
    EditOrganizationDetailsModalComponent,
    EditOrganizationModalComponent,
    EditRecordModalComponent,
    EditRecordTimesheetModalComponent,
    EditStakeholderDetailsModalComponent,
    EditUserModalComponent,
    OtherSourcesInfoModalComponent,
    RecordTypeInfoModalComponent,
    SearchQueryInfoModalComponent,
    // Shared
    AlertQueryComponent,
    BreadcrumbComponent,
    DeleteBtnComponent,
    DeleteBtnAdminComponent,
    LoadingComponent,
    PaginationComponent,
    ResultComponent,
    SearchFilterComponent,
    SearchInputComponent,
    SidebarComponent,
    SocialsComponent,
    SpinnerComponent,
    ToastComponent,
    // Pipes
    FileSizePipe,
    FilterPipe,
    FirstLetterUppercasePipe,
    IdCommissioneToRomanPipe,
    LastnPipe,
    LinkifyPipe,
    RomanizePipe,
    Split2charsPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: (): string => localStorage.getItem('token'),
        allowedDomains: ['localhost:3000', 'localhost:4200', 'app.utopialab.it'],
      }
    })
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    // Components
    ToastComponent,
    // Services
    AlertService,
    AuthGuardLogin,
    AuthGuardAdmin,
    AuthService,
    ClientService,
    EsService,
    FeedbackService,
    FileService,
    GovernmentService,
    GuidelineService,
    LegislatureService,
    MiscService,
    NotificationService,
    OrganizationService,
    PoliticianService,
    RuleService,
    UserService,
  ]
})

export class AppModule { }
